/**
 * Cookie consent for Google tags (Google Consent Mode v2).
 *
 * index.html sets every Google consent signal to "denied" BEFORE GTM loads,
 * and re-applies a saved "granted" choice on each page load. The banner
 * (components/ConsentBanner.tsx) records the visitor's choice here and sends
 * a consent update, so GA4 / Google Ads only set cookies after "Accept".
 *
 * Only relevant when GTM is configured (VITE_GTM_ID). Umami is cookieless and
 * needs no consent; the accessibility-preference storage is functional.
 *
 * Keep the storage key and the four signals in sync with the inline script
 * in index.html.
 */

export const CONSENT_STORAGE_KEY = "ab-consent";
export type ConsentChoice = "granted" | "denied";
/** Fired on window to re-open the banner (footer "Cookie settings"). */
export const OPEN_CONSENT_EVENT = "ab:open-consent";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/** True when the build loads Google Tag Manager, i.e. consent is needed. */
export function gtmEnabled(): boolean {
  return Boolean(import.meta.env.VITE_GTM_ID);
}

export function getConsent(): ConsentChoice | null {
  try {
    const v = localStorage.getItem(CONSENT_STORAGE_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

// window.gtag is defined by the inline consent-default script in index.html
// whenever GTM is configured; without GTM there is nothing to update.
function gtag(...args: unknown[]): void {
  if (typeof window !== "undefined" && typeof window.gtag === "function") window.gtag(...args);
}

export function setConsent(choice: ConsentChoice): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  } catch {
    // Storage blocked: the choice still applies for this page view.
  }
  gtag("consent", "update", {
    ad_storage: choice,
    ad_user_data: choice,
    ad_personalization: choice,
    analytics_storage: choice,
  });
}

export function openConsentSettings(): void {
  window.dispatchEvent(new Event(OPEN_CONSENT_EVENT));
}
